import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  TrendingUp, DollarSign, Percent, Clock, Building2, Briefcase,
  Plus, X, Pencil, ArrowRight,
} from 'lucide-react';
import api from '../config';
import WelcomeHero from '../components/WelcomeHero';

const CHART_COLORS = ['#a67f3d', '#0ea5e9', '#8b5cf6', '#059669', '#f59e0b', '#ec4899'];

// Portfolio-company stage taxonomy (mirrors src/core/stages.py)
export const STAGES = [
  { key: 'early', label: 'Early', badge: 'bg-violet-100 text-violet-700', desc: 'Product-market fit, growth over profit' },
  { key: 'growth', label: 'Growth', badge: 'bg-sky-100 text-sky-700', desc: 'Scaling revenue, path to profitability' },
  { key: 'mature', label: 'Mature', badge: 'bg-emerald-100 text-emerald-700', desc: 'Stable, cash-generative business' },
  { key: 'pre_exit', label: 'Pre-Exit', badge: 'bg-amber-100 text-amber-700', desc: 'Harvest prep — positioning for exit' },
];
export const stageInfo = (key) => STAGES.find(s => s.key === key) || null;
export const StageBadge = ({ stage, className = '' }) => {
  const info = stageInfo(stage);
  if (!info) return <span className={`badge badge-slate ${className}`}>No stage</span>;
  return <span className={`badge ${info.badge} ${className}`}>{info.label}</span>;
};

function KpiCard({ icon: Icon, label, value, sub, accent, subColor }) {
  return (
    <div className="kpi-card">
      <div className={`absolute top-0 left-0 w-1 h-full ${accent}`} />
      <div className="flex items-start justify-between">
        <div>
          <p className="kpi-label">{label}</p>
          <p className="kpi-value">{value}</p>
          {sub && <p className={`text-xs mt-1.5 ${subColor || 'text-slate-400'}`}>{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${accent.replace('bg-', 'bg-').replace('/80', '/10')}`}>
          <Icon className={`w-5 h-5 ${accent.replace('bg-', 'text-').replace('/80', '')}`} />
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPortco, setEditingPortco] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, trendRes] = await Promise.all([
        api.get('/api/portfolio/summary').catch(() => null),
        api.get('/api/portfolio/trends').catch(() => null),
      ]);
      setSummary(sumRes?.data);
      setTrends(trendRes?.data);
    } catch {
      setSummary(null);
      setTrends(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editingPortco) {
        await api.put(`/api/portfolio/companies/${editingPortco.portco_id}`, formData);
      } else {
        await api.post('/api/portfolio/companies', formData);
      }
      setShowAddForm(false);
      setEditingPortco(null);
      await fetchData();
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const handleDelete = async (portcoId) => {
    if (!window.confirm('Mark this portfolio company as exited? This removes it from active tracking.')) return;
    try {
      await api.delete(`/api/portfolio/companies/${portcoId}`);
      await fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const fmtM = (v) => `$${(v / 1e6).toFixed(1)}M`;
  const fmtPct = (v) => `${v.toFixed(1)}%`;
  const chartData = (trends?.quarterly_datapoints || []).slice(-8);
  const portcos = (summary.portco_list || []).filter(p => p.status !== 'exited');
  const visiblePortcos = stageFilter === 'all' ? portcos : portcos.filter(p => p.stage === stageFilter);

  return (
    <div className="page-container">
      {/* Welcome experience: greeting, Ask Caply, quick tiles, onboarding */}
      <WelcomeHero />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="page-title">Current Portfolio</h2>
          <p className="page-subtitle">How each of your companies is doing, at a glance</p>
        </div>
        <button onClick={() => { setEditingPortco(null); setShowAddForm(true); }} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Portco
        </button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={DollarSign} label="Total Revenue" value={fmtM(summary.total_revenue)} sub={`${summary.portco_count} companies`} accent="bg-brand-600/80" />
        <KpiCard icon={TrendingUp} label="Total EBITDA" value={fmtM(summary.total_ebitda)} accent="bg-emerald-500/80" subColor="text-emerald-600" />
        <KpiCard icon={Briefcase} label="Invested Capital" value={fmtM(summary.total_invested_capital)} sub={`${summary.portco_count} active`} accent="bg-navy-600/80" />
        <KpiCard icon={TrendingUp} label="Blended MOIC" value={`${summary.blended_moic?.toFixed(2) || '0.00'}x`} accent="bg-gold-500/80" subColor="text-emerald-600" />
        <KpiCard icon={Percent} label="Blended IRR" value={fmtPct(summary.blended_irr || 0)} accent="bg-purple-500/80" subColor="text-emerald-600" />
        <KpiCard icon={Clock} label="Avg Hold" value={`${summary.avg_hold_period?.toFixed(1) || '0'} yrs`} accent="bg-sky-500/80" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="chart-container">
          <div className="mb-4">
            <h3 className="chart-title">Revenue & EBITDA Trend ($M)</h3>
            <p className="chart-subtitle">Quarterly aggregated portfolio performance</p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData} margin={{ left: -10, right: 10, top: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Line type="monotone" dataKey="revenue" stroke="#a67f3d" strokeWidth={2.5} dot={{ r: 4, fill: '#a67f3d' }} name="Revenue ($M)" />
              <Line type="monotone" dataKey="ebitda" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, fill: '#059669' }} name="EBITDA ($M)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <div className="mb-4">
            <h3 className="chart-title">EBITDA by Company ($M)</h3>
            <p className="chart-subtitle">Current earnings breakdown across portfolio</p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={portcos.map(p => ({ name: p.portco_name.split(' ')[0], ebitda: p.ebitda / 1e6 }))} margin={{ left: -10, right: 10, top: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} cursor={{ fill: '#f1f5f9' }} />
              <Bar dataKey="ebitda" radius={[6, 6, 0, 0]} name="EBITDA ($M)">
                {portcos.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Company cards */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-navy-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-brand-600" />
          Portfolio Companies
          <span className="text-sm text-slate-400 font-normal">({visiblePortcos.length} active)</span>
        </h3>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setStageFilter('all')}
            className={`badge cursor-pointer transition ${stageFilter === 'all' ? 'bg-navy-900 text-white' : 'badge-slate hover:bg-slate-200'}`}
          >
            All
          </button>
          {STAGES.map(st => (
            <button
              key={st.key}
              onClick={() => setStageFilter(stageFilter === st.key ? 'all' : st.key)}
              title={st.desc}
              className={`badge cursor-pointer transition ${stageFilter === st.key ? st.badge : 'badge-slate opacity-70 hover:opacity-100'}`}
            >
              {st.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visiblePortcos.map(p => (
          <div
            key={p.portco_id}
            className="card group cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/portfolio/${p.portco_id}`)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center text-white font-bold">
                  {p.portco_name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-base font-bold text-navy-900 group-hover:text-brand-700 transition">{p.portco_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="badge badge-slate">{p.industry}</span>
                    <StageBadge stage={p.stage} />
                    {p.deal_origin && (
                      <span className="badge badge-blue text-xs">From Deal</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingPortco(p); setShowAddForm(true); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-100 transition"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(p.portco_id); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="text-right ml-2">
                  <p className="text-xs text-slate-400">Hold</p>
                  <p className="text-sm font-mono font-semibold text-navy-900">{p.hold_period_years?.toFixed(1) || '0'} yrs</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">Revenue</span>
                  <span className="text-sm font-mono font-semibold text-navy-900">{fmtM(p.revenue)}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">EBITDA</span>
                  <span className="text-sm font-mono font-semibold text-emerald-600">{fmtM(p.ebitda)}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">Margin</span>
                  <span className="text-sm font-mono font-semibold text-slate-600">{((p.ebitda_margin || 0) * 100).toFixed(1)}%</span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">MOIC</span>
                  <span className="text-sm font-mono font-semibold text-gold-600">{p.moic?.toFixed(2) || '0'}x</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">IRR</span>
                  <span className={`text-sm font-mono font-semibold ${p.irr > 20 ? 'text-emerald-600' : 'text-amber-600'}`}>{p.irr?.toFixed(1) || '0'}%</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">Invested</span>
                  <span className="text-sm font-mono font-semibold text-navy-600">{fmtM(p.initial_investment)}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="flex-1 mr-3">
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-gradient-to-r from-brand-500 to-emerald-500 h-2 rounded-full" style={{ width: `${Math.min((p.moic || 0) / 4 * 100, 100)}%` }} />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-slate-400">MOIC progress</span>
                  <span className="text-[10px] font-mono font-semibold text-brand-700">{p.moic?.toFixed(2) || '0'}x / 4.0x</span>
                </div>
              </div>
              <span className="text-xs text-brand-600 font-medium flex items-center gap-0.5 group-hover:gap-1 transition-all">
                View
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Portco Modal */}
      {showAddForm && (
        <PortcoFormModal
          portco={editingPortco}
          onSave={handleSave}
          onClose={() => { setShowAddForm(false); setEditingPortco(null); }}
        />
      )}
    </div>
  );
}

// ── Add/Edit Portco Form Modal ──
function PortcoFormModal({ portco, onSave, onClose }) {
  const [form, setForm] = useState({
    portco_name: portco?.portco_name || '',
    industry: portco?.industry || 'Technology',
    geography: portco?.geography || 'North America',
    description: portco?.description || '',
    lead_partner: portco?.lead_partner || '',
    initial_investment: portco?.initial_investment || '',
    acquisition_date: portco?.acquisition_date || new Date().toISOString().slice(0, 10),
    revenue: portco?.revenue || '',
    ebitda: portco?.ebitda || '',
    moic: portco?.moic || '1.0',
    irr: portco?.irr || '0',
    stage: portco?.stage || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      stage: form.stage || null,
      initial_investment: parseFloat(form.initial_investment) || 0,
      revenue: parseFloat(form.revenue) || 0,
      ebitda: parseFloat(form.ebitda) || 0,
      moic: parseFloat(form.moic) || 1.0,
      irr: parseFloat(form.irr) || 0,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-navy-900">
            {portco ? 'Edit Portfolio Company' : 'Register New Portco'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="form-label">Company Name *</label>
            <input
              className="form-input"
              required
              value={form.portco_name}
              onChange={e => setForm({...form, portco_name: e.target.value})}
              placeholder="e.g. Acme Software Inc."
            />
          </div>
          <div>
            <label className="form-label">Stage</label>
            <select className="form-input" value={form.stage} onChange={e => setForm({...form, stage: e.target.value})}>
              <option value="">Auto-infer from financials</option>
              {STAGES.map(st => <option key={st.key} value={st.key}>{st.label} — {st.desc}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Industry</label>
              <select className="form-input" value={form.industry} onChange={e => setForm({...form, industry: e.target.value})}>
                <option>Technology</option>
                <option>Enterprise SaaS</option>
                <option>HealthTech</option>
                <option>Industrial Automation</option>
                <option>CleanTech SaaS</option>
                <option>Financial Services</option>
                <option>Consumer</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="form-label">Geography</label>
              <select className="form-input" value={form.geography} onChange={e => setForm({...form, geography: e.target.value})}>
                <option>North America</option>
                <option>Europe</option>
                <option>Asia Pacific</option>
                <option>Latin America</option>
                <option>Global</option>
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              rows={2}
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Brief company description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Initial Investment ($) *</label>
              <input
                className="form-input"
                type="number"
                required
                value={form.initial_investment}
                onChange={e => setForm({...form, initial_investment: e.target.value})}
                placeholder="35000000"
              />
            </div>
            <div>
              <label className="form-label">Acquisition Date *</label>
              <input
                className="form-input"
                type="date"
                required
                value={form.acquisition_date}
                onChange={e => setForm({...form, acquisition_date: e.target.value})}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Annual Revenue ($)</label>
              <input
                className="form-input"
                type="number"
                value={form.revenue}
                onChange={e => setForm({...form, revenue: e.target.value})}
                placeholder="45000000"
              />
            </div>
            <div>
              <label className="form-label">EBITDA ($)</label>
              <input
                className="form-input"
                type="number"
                value={form.ebitda}
                onChange={e => setForm({...form, ebitda: e.target.value})}
                placeholder="12800000"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">MOIC (x)</label>
              <input
                className="form-input"
                type="number"
                step="0.1"
                value={form.moic}
                onChange={e => setForm({...form, moic: e.target.value})}
                placeholder="1.0"
              />
            </div>
            <div>
              <label className="form-label">IRR (%)</label>
              <input
                className="form-input"
                type="number"
                step="0.1"
                value={form.irr}
                onChange={e => setForm({...form, irr: e.target.value})}
                placeholder="0"
              />
            </div>
          </div>
          <div>
            <label className="form-label">Lead Partner</label>
            <input
              className="form-input"
              value={form.lead_partner}
              onChange={e => setForm({...form, lead_partner: e.target.value})}
              placeholder="Sarah Chen"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1">
              {portco ? 'Save Changes' : 'Register Portco'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
